import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import ProfileWithBackButtonHeader from "../header/ProfileWithBackButtonHeader";
import CouponItemDetail from "../coupon/CouponItemDetail";
import SelectStoreLocationModal from "./SelectStoreLocationModal";
import RedeemCouponModal from "./RedeemCouponModal";
import DangerButton from "../common/DangerButton";
import LoadingBar from "../LoadingBar";

import { ItemHelper } from "../helpers/item-helper";
import { useLiffProfile } from "../../context/LiffProfileContext";

const BcCouponDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();

  const [bcCouponDetail, setBcCouponDetail] = useState(null);
  const [error, setError] = useState(null);
  const [getPoint, setPointBalance] = useState(0);
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const [locationCode, setLocationCode] = useState(undefined);
  const [ready, setReady] = useState(false);

  const [showSelectStoreLocationModal, setShowSelectStoreLocationModal] =
    useState(false);
  const [showRedeemCouponModal, setShowRedeemCouponModal] = useState(false);
  const [redeemBcCouponError, setRedeemBcCouponError] = useState(null);
  const [redeemBcCouponResult, setRedeemBcCouponResult] = useState(null);
  const [bcCouponUrlOrigin, setBcCouponUrlOrigin] = useState(null);

  const seniorLayout = localStorage.getItem("seniorLayout") === "true";
  const lang = i18n.language.toUpperCase();

  const isRedeemable = bcCouponDetail?.redeemable;
  const isPremiumCoupon = bcCouponDetail?.categoryCode === "12";
  const isSpecialForYou = bcCouponDetail?.categoryCode === "13";

  const redeemDesc = ItemHelper.getRedeemDesc(bcCouponDetail, i18n, lang);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
      "x-user-id": userId,
      "Accept-Language": i18n.language.toUpperCase(),
    }),
    [authToken, userId, i18n.language]
  );

  const fetchPointData = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/users/current/clm-point`;
      const response = await axios.get(apiUrl, { headers });
      setPointBalance(response.data?.data || 0);
    } catch (error) {
      console.error("Error fetching point:", error);
    }
  };

  const fetchClmCouponDetail = async () => {
    try {
      setLoadingCoupon(true);

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/clm-coupons/${id}`;
      const response = await axios.get(apiUrl, { headers });
      if (response.data?.data) {
        setBcCouponDetail(response.data.data);
        // ✅ GA4 Event: BC Coupon Detail View
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "all_coupon_detail_view",
          coupon_id: response.data.data.id,
          coupon_type: response.data.data.couponType,
          category_code: response.data.data.categoryCode,
          point_redeem: response.data.data.pointRedeem,
          redeemable: response.data.data.redeemable,
        });
      } else {
        setError("No data found.");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoadingCoupon(false);
  };

  function mapRedemptionErrorCode(message) {
    const fallbackMsg = t("REDEMPTIONS_ERROR_CODE.DEFAULT");
    if (message) {
      const messages = message.split("|").map((v) => v.trim());
      if (messages.length >= 2) {
        const errorKey = "REDEMPTIONS_ERROR_CODE." + messages[0];
        const translated = t(errorKey, { defaultValue: fallbackMsg });
        return translated;
      }
    }
    return fallbackMsg;
  }

  useEffect(() => {
    if (authToken && userId) {
      fetchClmCouponDetail();
      fetchPointData();
    }
  }, [id, authToken, userId]);

  // Redeem coupon logic
  const redeemBcCoupon = async ({ couponId, idCard, locationCode }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/clm-coupons/${couponId}/redeem`,
        { idCard, locationCode },
        { headers }
      );

      const { statusCode, message, data } = response.data;

      if (statusCode === 200 && data?.responseCode === "000") {
        setRedeemBcCouponResult(data);
        setRedeemBcCouponError(null);
        return data;
      } else {
        handleRedeemError(statusCode, message);
      }
    } catch (err) {
      //setRedeemBcCouponError("Unexpected error. Please try again.");
    }
  };

  const handleRedeemError = (statusCode, message) => {
    let errorMsg = t("redeem_coupon_error_default");

    if ([400, 404, 406].includes(statusCode)) {
      switch (message) {
        case "ID_CARD_MISMATCHED":
          errorMsg = t("redeem_coupon_error_id_card_mismatched");
          break;
        case "COUPON_NOT_REDEEMABLE":
          errorMsg = t("redeem_coupon_error_general");
          break;
        case "EVENT_ENDED":
        case "POINT_NOT_ENOUGH":
        case "TRY_AGAIN_LATER":
          errorMsg = t(message.toLowerCase());
          break;
        default:
          const parts = message?.split("|") || [];
          errorMsg = parts.length >= 3 ? parts[2] : message;
      }
    } else if (statusCode === 500) {
      errorMsg = mapRedemptionErrorCode(message);
    }

    setRedeemBcCouponError(errorMsg);
    setRedeemBcCouponResult(null);
  };

  const clearRedeemBcCouponError = () => setRedeemBcCouponError(null);
  const clearRedeemBcCouponResult = () => setRedeemBcCouponResult(null);
  const clearBcCouponUrlOrigin = () => setBcCouponUrlOrigin(null);

  const handleGoBack = () => {
    clearRedeemBcCouponError();
    setBcCouponDetail(null);
    navigate(bcCouponUrlOrigin || "/bc-coupons");
    clearBcCouponUrlOrigin();
  };

  const gotoMyCouponsPage = () => {
    const category = isPremiumCoupon
      ? "premium_coupons"
      : isSpecialForYou
      ? "special_for_you"
      : "";
    navigate(
      `/my-coupons?couponType=${bcCouponDetail.couponType}${
        category ? `&category=${category}` : ""
      }`
    );
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    // ✅ GA4 Event: BC Coupon Redeem Click
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "all_coupon_redeem_click",
      coupon_id: bcCouponDetail.id,
      coupon_type: bcCouponDetail.couponType,
      category_code: bcCouponDetail.categoryCode,
    });
    if (isPremiumCoupon) {
      setShowSelectStoreLocationModal(true);
    } else {
      await redeemCoupon();
    }
  };

  const redeemCoupon = async () => {
    setLoadingCoupon(true);
    if (bcCouponDetail.pointRedeem === 0) {
      const result = await redeemBcCoupon({
        couponId: bcCouponDetail.id,
        idCard: undefined,
        locationCode,
      });
      // if (redeemBcCouponResult) {
      if (result) {
        clearRedeemBcCouponResult();
        clearRedeemBcCouponError();
        gotoMyCouponsPage();
      }
    } else {
      setShowRedeemCouponModal(true);
    }
    setLoadingCoupon(false);
  };

  const handleRedeemSuccess = async () => {
    //setLoadingCoupon(true);
    //setShowRedeemCouponModal(false);
    //clearRedeemBcCouponResult();
    await fetchPointData();
    // ✅ GA4 Event: BC Coupon Redeem Success
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "all_coupon_redeemed",
      coupon_id: bcCouponDetail.id,
      coupon_type: bcCouponDetail.couponType,
      category_code: bcCouponDetail.categoryCode,
      point_redeem: bcCouponDetail.pointRedeem,
    });
    gotoMyCouponsPage();
  };

  const handleRedeemCancel = () => {
    clearRedeemBcCouponError();
    setShowRedeemCouponModal(false);
    if (isPremiumCoupon && locationCode) {
      setShowSelectStoreLocationModal(true);
    }
  };

  const handleSelectLocation = async (code) => {
    setLocationCode(code);
    setShowSelectStoreLocationModal(false);
    await redeemCoupon();
  };

  return (
    <div className="h-screen relative overflow-y-auto overscroll-contain bg-secondary-background-gray">
      <ProfileWithBackButtonHeader onClick={handleGoBack} />
      {bcCouponDetail ? (
        <>
          <div className="w-full pb-20">
            <CouponItemDetail value={bcCouponDetail} />
          </div>

          <div className="fixed bottom-0 w-full max-w-md py-5 z-[888] flex flex-col justify-center items-center gap-y-4 bg-white">
            {redeemBcCouponError && (
              <div
                className={`w-full font-normal text-danger text-center break-words px-5 ${
                  seniorLayout ? "text-base" : "text-sm"
                }`}
                dangerouslySetInnerHTML={{ __html: redeemBcCouponError }}
              />
            )}

            {isRedeemable ? (
              <div className="w-48 h-13">
                <DangerButton
                  buttonName={t("redeem")}
                  disabled={!isRedeemable}
                  seniorLayout={seniorLayout}
                  onClick={(e) => handleRedeem(e)}
                />
              </div>
            ) : (
              <div
                className={`font-normal text-danger text-center ${
                  seniorLayout ? "text-lg" : "text-sm"
                }`}
                dangerouslySetInnerHTML={{ __html: redeemDesc }}
              />
            )}
          </div>
        </>
      ) : (
        !loadingCoupon && (
          <div className="w-full text-center py-4">{t("data_not_found")}</div>
        )
      )}
      <LoadingBar loading={loadingCoupon} label="Loading..." />

      <SelectStoreLocationModal
        show={showSelectStoreLocationModal}
        couponDetail={bcCouponDetail}
        onOk={handleSelectLocation}
        onCancel={() => setShowSelectStoreLocationModal(false)}
      />

      {showRedeemCouponModal && (
        <RedeemCouponModal
          show={showRedeemCouponModal}
          value={bcCouponDetail}
          locationCode={locationCode}
          onSuccess={handleRedeemSuccess}
          onCancel={handleRedeemCancel}
          redeemBcCoupon={redeemBcCoupon}
          redeemBcCouponError={redeemBcCouponError}
          redeemBcCouponResult={redeemBcCouponResult}
          clearRedeemBcCouponError={clearRedeemBcCouponError}
          setLoadingCoupon={setLoadingCoupon}
        />
      )}
    </div>
  );
};

export default BcCouponDetail;
